(() => {
  const giftVariant = 44721317544121;
  const threshold = 49900;
  const root = window.Shopify?.routes?.root || '/';
  const declineKey = 'mu4life-gift-declined';
  const declineAttribute = '_mu4life_gift_declined';
  let queue = Promise.resolve();
  let pending = 0;
  let refreshPromise;
  let declined = false;
  let leaving = false;
  let failedSignature;
  let removed = false;
  const identities = new Map();
  try { declined = sessionStorage.getItem(declineKey) === 'true'; } catch {}

  const isBag = item => Number(item.variant_id) === giftVariant;
  const isGift = item => isBag(item) && item.properties?._mu4life_gift === 'true';
  const eligible = cart => cart.items.reduce((sum, item) =>
    sum + (isBag(item) ? 0 : Number(item.original_line_price)), 0) >= threshold;
  const free = item => item.quantity === 1 && Number(item.final_line_price) === 0;
  const signature = cart => JSON.stringify(cart.items.filter(item => !isBag(item))
    .map(item => [item.variant_id, item.quantity, item.original_line_price]));
  const identity = item => JSON.stringify([Number(item.variant_id),
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
    item.selling_plan_allocation?.selling_plan?.id || null]);

  function remember(cart) {
    cart.items?.forEach(item => {
      if (item.key) identities.set(item.key, identity(item));
    });
    return cart;
  }

  async function request(path, body) {
    const signal = window.AbortSignal?.timeout?.(12000);
    const response = await fetch(root + path, body ? {
      signal,
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    } : { cache: 'no-store', signal });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || 'Cart request failed');
    }
    return remember(data);
  }

  function run(work) {
    pending++;
    const result = queue.then(() => window.navigator?.locks
      ? window.navigator.locks.request('mu4life-cart', work) : work());
    queue = result.catch(() => {});
    return result.finally(() => { pending--; });
  }

  function rememberDecline(value) {
    declined = value;
    try {
      if (value) sessionStorage.setItem(declineKey, 'true');
      else sessionStorage.removeItem(declineKey);
    } catch {}
  }

  async function decline(id) {
    const cart = await request('cart.js');
    if (!cart.items.some(item => item.key === id && isGift(item))) return;
    rememberDecline(true);
    try { await request('cart/update.js', { attributes: { [declineAttribute]: 'true' } }); }
    catch (error) { console.error('MU4LIFE gift choice:', error); }
  }

  async function change(body) {
    const cart = await request('cart.js');
    let line = cart.items.find(item => item.key === body.id);
    if (!line) {
      const matches = cart.items.filter(item => identity(item) === identities.get(body.id));
      if (matches.length === 1) line = matches[0];
      else if (matches.length && matches.every(isGift)) line = matches.find(free) || matches[0];
      else if (!matches.length && body.quantity === 0) return cart;
      else throw new Error(window.cartStrings?.error || 'Cart line changed');
    }
    if (body.quantity === 0 && isGift(line)) await decline(line.key);
    return request('cart/change.js', { ...body, id: line.key });
  }

  async function removeGift(cart, gift, keepExtras = true) {
    const quantity = keepExtras ? Math.max(0, gift.quantity - 1) : 0;
    const body = { id: gift.key, quantity };
    if (quantity) body.properties = { ...gift.properties, _mu4life_gift: '' };
    return request('cart/change.js', body);
  }

  async function reconcile(cart) {
    remember(cart);
    if (cart.attributes?.[declineAttribute] === 'true') rememberDecline(true);
    removed = false;
    let mutations = 0;
    while (mutations++ < 20) {
      const gifts = cart.items.filter(isGift);
      if (!eligible(cart) || declined) {
        if (gifts.length) {
          cart = await removeGift(cart, gifts[0]);
          removed = !declined;
          continue;
        }
        break;
      }
      const extra = gifts.find(item => item.quantity > 1);
      if (extra) {
        cart = await removeGift(cart, extra);
        continue;
      }
      if (gifts.length > 1) {
        const keep = gifts.find(free) || gifts[0];
        cart = await removeGift(cart, gifts.find(item => item.key !== keep.key), false);
        continue;
      }
      if (gifts.length) {
        if (!free(gifts[0])) {
          failedSignature = signature(cart);
          cart = await removeGift(cart, gifts[0]);
          removed = true;
        }
        break;
      }
      if (failedSignature === signature(cart)) break;
      try {
        await request('cart/add.js', { items: [{ id: giftVariant, quantity: 1,
          properties: { _mu4life_gift: 'true' } }] });
      } catch (error) {
        failedSignature = signature(cart);
        console.error('MU4LIFE gift unavailable:', error);
        cart = await request('cart.js');
        if (!cart.items.some(isGift)) break;
      }
      cart = await request('cart.js');
    }
    if (!cart.items.length) {
      rememberDecline(false);
      failedSignature = undefined;
      if (cart.attributes?.[declineAttribute]) {
        cart = await request('cart/update.js', { attributes: { [declineAttribute]: '' } });
      }
    }
    return cart;
  }

  async function sections(ids) {
    return request('cart?sections=' + encodeURIComponent([...new Set(ids)].join(',')));
  }

  async function refreshSections() {
    const items = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (items) await items.onCartUpdate();
    else {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.renderContents({ sections: await sections(drawer.getSectionsToRender().map(s => s.id)) }, false);
    }
  }

  function renderStatus() {
    document.querySelectorAll('.mu-cart-gift').forEach(element => element.remove());
    if (!removed) return;
    const host = document.querySelector('#main-cart-footer') || document.querySelector('#CartDrawer .drawer__footer');
    if (!host) return;
    const message = document.createElement('p');
    message.className = 'mu-cart-gift';
    message.setAttribute('role', 'status');
    message.textContent = window.mu4lifeGiftStrings.removed;
    host.prepend(message);
  }

  function refresh() {
    if (leaving) return Promise.resolve();
    if (refreshPromise) return refreshPromise;
    refreshPromise = run(async () => {
      const before = await request('cart.js');
      const cart = await reconcile(before);
      if (cart !== before) await refreshSections();
      renderStatus();
      return cart;
    }).catch(error => console.error('MU4LIFE gift cart:', error))
      .finally(() => { refreshPromise = undefined; });
    return refreshPromise;
  }

  async function afterAdd(response, renderer) {
    const cart = await reconcile(await request('cart.js'));
    if (!renderer) return response;
    return { ...response, id: cart.items.some(isGift) ? undefined : response.id,
      sections: await sections(renderer.getSectionsToRender().map(s => s.id)) };
  }

  async function prepareCheckout() {
    const cart = await reconcile(await request('cart.js'));
    if (!cart.items.length || cart.items.some(item => isGift(item) && !free(item))) {
      throw new Error(window.cartStrings?.error || 'Cart verification failed');
    }
    return cart;
  }

  function showError(button, error) {
    const form = button?.closest('product-form');
    if (form?.handleErrorMessage) form.handleErrorMessage(error.message);
    else {
      const host = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (host) host.textContent = window.cartStrings?.error || error.message;
    }
    console.error('MU4LIFE checkout:', error);
  }

  async function checkout(button, form) {
    if (leaving || button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;
    leaving = true;
    if (button) button.disabled = true;
    try {
      await run(async () => {
        if (form) {
          const response = await fetch(root + 'cart/add.js', { method: 'POST', body: new FormData(form) });
          const added = await response.json();
          if (!response.ok || added.status || added.errors) throw new Error(added.description || added.message || window.cartStrings.error);
        }
        await prepareCheckout();
        window.location.assign(root + 'checkout');
      });
    } catch (error) {
      leaving = false;
      await refresh();
      showError(button, error);
    } finally {
      if (button) button.disabled = false;
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"], a[href]');
    if (!button) return;
    if (button.tagName === 'A') {
      const url = new URL(button.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== root + 'checkout') return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
    }
    event.preventDefault();
    checkout(button);
  }, true);

  document.addEventListener('submit', event => {
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    checkout(event.submitter);
  }, true);

  window.mu4lifeGiftCart = { get busy() { return pending > 0; }, get leaving() { return leaving; },
    eligible, reconcile, refresh, decline, change, run, afterAdd, prepareCheckout,
    buyNow: (form, button) => checkout(button, form) };
  subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
  window.addEventListener('pageshow', () => { leaving = false; refresh(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh();
  });
  refresh();
})();
